import React, { FC, ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  FileTypeUploads,
  FileUploads,
  FollowUpEmails,
  FranzeeUser,
  OrganizationManagementRecord,
  OrganizationSetup,
  SftpAccess,
  SupplierRegistrationPortal,
} from '@app/generated/svm';

const CARD_TITLES = {
  organizationManagement: 'Organization Management',
  portal: 'Supplier Registration Portal',
  followUpEmails: 'Follow-Up Emails',
  sftpAccess: 'SFTP Access',
  fileUploads: 'File Uploads',
};

const SFTP_REMINDERS = ['The organization’s folder in the SFTP S3 bucket', 'An SFTP account and SSH key (DevOps)'];

interface OrganizationSetupCardsProps {
  organizationSetup: OrganizationSetup;
}

const OrganizationSetupCards: FC<OrganizationSetupCardsProps> = ({ organizationSetup }) => {
  return (
    <CardsGrid dataTestId='organization-setup-cards'>
      <SetupCard title={CARD_TITLES.organizationManagement} dataTestId='organization-setup-organization-management'>
        {renderOrganizationManagementRecord(organizationSetup.organizationManagementRecord)}
      </SetupCard>
      <SetupCard title={CARD_TITLES.portal} dataTestId='organization-setup-portal'>
        {renderPortal(organizationSetup.supplierRegistrationPortal)}
      </SetupCard>
      <SetupCard title={CARD_TITLES.followUpEmails} dataTestId='organization-setup-follow-up-emails'>
        {renderFollowUpEmails(organizationSetup.followUpEmails)}
      </SetupCard>
      <SetupCard title={CARD_TITLES.sftpAccess} dataTestId='organization-setup-sftp-access'>
        {renderSftpAccess(organizationSetup.sftpAccess)}
      </SetupCard>
      <SetupCard title={CARD_TITLES.fileUploads} dataTestId='organization-setup-file-uploads'>
        {renderFileUploads(organizationSetup.fileUploads)}
      </SetupCard>
    </CardsGrid>
  );
};

export default OrganizationSetupCards;

export const OrganizationSetupCardsSkeleton: FC = () => (
  <CardsGrid dataTestId='organization-setup-cards-skeleton'>
    {Object.values(CARD_TITLES).map((title) => (
      <SetupCard key={title} title={title} dataTestId='organization-setup-card-skeleton'>
        <Skeleton className='h-4 w-3/5' />
        <Skeleton className='h-4 w-2/5' />
        <Skeleton className='h-4 w-1/2' />
      </SetupCard>
    ))}
  </CardsGrid>
);

function renderOrganizationManagementRecord(record: OrganizationManagementRecord | null): ReactNode {
  if (!record) {
    return <EmptyNote>No record: the validation system doesn’t know this organization yet</EmptyNote>;
  }

  return (
    <Fields>
      <Field label='Uses supplier validation'>
        {record.usesSupplierValidation ? 'Yes, listed in the validation system' : 'No'}
      </Field>
      <Field label='Group'>{record.groupName ?? 'None'}</Field>
    </Fields>
  );
}

function renderPortal(portal: SupplierRegistrationPortal | null): ReactNode {
  if (!portal) {
    return <EmptyNote>No portal</EmptyNote>;
  }

  return (
    <Fields>
      <Field label='Subdomain'>{portal.subdomain ?? 'None'}</Field>
      <Field label='Reference id'>{portal.referenceId}</Field>
      <Field label='State'>{portal.disabled ? 'Off' : 'On'}</Field>
      <Field label='Branded'>{portal.branded ? 'Yes' : 'No'}</Field>
      <Field label='Finicity open banking'>{toFinicityText(portal.useFinicityOpenBanking)}</Field>
    </Fields>
  );
}

function toFinicityText(useFinicityOpenBanking: boolean | null): string {
  if (useFinicityOpenBanking === null) {
    return 'Environment default';
  }

  return useFinicityOpenBanking ? 'On' : 'Off';
}

function renderFollowUpEmails(followUpEmails: FollowUpEmails | null): ReactNode {
  if (!followUpEmails) {
    return <EmptyNote>The follow-up email sender isn’t configured in this environment</EmptyNote>;
  }

  return (
    <Fields>
      <Field label='Sent'>{followUpEmails.organizationIncluded ? 'Yes' : 'No'}</Field>
      <Field label='Why'>{toFollowUpEmailsReason(followUpEmails)}</Field>
    </Fields>
  );
}

function toFollowUpEmailsReason(followUpEmails: FollowUpEmails): string {
  if (followUpEmails.runOnAllOrganizations) {
    return followUpEmails.organizationIncluded
      ? 'The sender runs on every organization it doesn’t exclude'
      : 'Excluded; the sender runs on every other organization';
  }

  return followUpEmails.organizationIncluded
    ? 'On the sender’s list of organizations'
    : 'Not on the sender’s list of organizations';
}

function renderSftpAccess(sftpAccess: SftpAccess): ReactNode {
  const hasSftp = sftpAccess.fileBasedSftp || sftpAccess.externalValidationSftp;

  return (
    <>
      <Fields>
        <Field label='File-based SFTP'>{sftpAccess.fileBasedSftp ? 'Yes' : 'No'}</Field>
        <Field label='External validation SFTP'>{sftpAccess.externalValidationSftp ? 'Yes' : 'No'}</Field>
        {sftpAccess.fileBasedSftp && (
          <Field label='Payment check portal'>
            {sftpAccess.paymentCheckPortal ? 'Yes' : 'No, so mortee refuses SFTP payment files'}
          </Field>
        )}
        {hasSftp && <Field label='franzee’s user'>{renderFranzeeUser(sftpAccess.franzeeUser)}</Field>}
      </Fields>
      <FootNote>
        <div>Granted as organization services in Backee</div>
        {hasSftp && (
          <div data-slot='setup-sftp-reminder'>
            <div>SFTP also needs, outside this page:</div>
            {SFTP_REMINDERS.map((reminder) => (
              <div key={reminder} className='pl-3'>
                • {reminder}
              </div>
            ))}
          </div>
        )}
      </FootNote>
    </>
  );
}

// franzee processes the organization's SFTP files as this user
function renderFranzeeUser(franzeeUser: FranzeeUser | null): ReactNode {
  if (!franzeeUser) {
    return 'Can’t check: Backee didn’t give you this organization’s users';
  }

  if (franzeeUser.organizationMember && !franzeeUser.missingRoles.length) {
    return 'Ready';
  }

  return (
    <>
      <div>{franzeeUser.organizationMember ? 'Missing roles' : 'Not a member of the organization'}</div>
      <ValueLines lines={franzeeUser.missingRoles} />
    </>
  );
}

function renderFileUploads(fileUploads: FileUploads): ReactNode {
  return (
    <>
      <FileTypeUploadsFields title='MVF files' fileTypeUploads={fileUploads.mvf} dataSlot='setup-mvf-uploads' />
      <FileTypeUploadsFields title='Payment files' fileTypeUploads={fileUploads.payment} dataSlot='setup-payment-uploads' />
    </>
  );
}

const FileTypeUploadsFields: FC<{ title: string; fileTypeUploads: FileTypeUploads; dataSlot: string }> = ({
  title,
  fileTypeUploads,
  dataSlot,
}) => (
  <div className='flex flex-col gap-1' data-slot={dataSlot}>
    <div className='text-sm text-muted-foreground'>{title}</div>
    <Fields>
      <Field label='Columns'>
        <div>{toColumnsText(fileTypeUploads)}</div>
        <ValueLines
          lines={Object.entries(fileTypeUploads.organizationColumns).map(([field, header]) => `${field} ← ${header}`)}
        />
      </Field>
      <Field label='Upload emails'>{toUploadEmailsText(fileTypeUploads)}</Field>
    </Fields>
  </div>
);

function toColumnsText(fileTypeUploads: FileTypeUploads): string {
  if (fileTypeUploads.usesOrganizationColumns) {
    return 'The organization’s own';
  }

  // mortee reads an unknown format as the default one, without an error
  return fileTypeUploads.unrecognizedFormat
    ? `Default, since mortee doesn’t know the format “${fileTypeUploads.unrecognizedFormat}”`
    : 'Default';
}

function toUploadEmailsText(fileTypeUploads: FileTypeUploads): string {
  if (!fileTypeUploads.uploadEmailsEnabled) {
    return 'Off';
  }

  return fileTypeUploads.uploadEmailAddresses || 'On, but no addresses';
}

const ValueLines: FC<{ lines: string[] }> = ({ lines }) => (
  <>
    {lines.map((line) => (
      <div key={line} className='text-sm text-muted-foreground'>
        {line}
      </div>
    ))}
  </>
);

const CardsGrid: FC<{ dataTestId: string; children: ReactNode }> = ({ dataTestId, children }) => (
  <div className='grid grid-cols-1 gap-4 md:grid-cols-2' data-testid={dataTestId}>
    {children}
  </div>
);

const SetupCard: FC<{ title: string; dataTestId: string; children: ReactNode }> = ({ title, dataTestId, children }) => (
  <Card data-testid={dataTestId}>
    <CardHeader>
      <CardTitle>{title}</CardTitle>
    </CardHeader>
    <CardContent className='flex flex-col gap-2'>{children}</CardContent>
  </Card>
);

const Fields: FC<{ children: ReactNode }> = ({ children }) => (
  <div className='grid grid-cols-[auto_1fr] gap-x-4 gap-y-1'>{children}</div>
);

// Not dt/dd: antd's unlayered base styles override Tailwind's layered utilities on those
const Field: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <>
    <div className='font-semibold' data-slot='setup-field-label'>
      {label}
    </div>
    <div data-slot='setup-field-value'>{children}</div>
  </>
);

const EmptyNote: FC<{ children: ReactNode }> = ({ children }) => <div className='text-muted-foreground'>{children}</div>;

const FootNote: FC<{ children: ReactNode }> = ({ children }) => (
  <div className='mt-2 flex flex-col gap-3' data-slot='setup-foot-note'>
    <div className='h-px w-full bg-border' data-slot='setup-foot-note-separator' />
    <div className='flex flex-col gap-2 text-sm text-muted-foreground'>{children}</div>
  </div>
);
